import React from 'react';
import {
  Clock,
  CheckCircle,
  Truck,
  FileText,
  CreditCard,
  XCircle,
  Package
} from 'lucide-react';

interface WorkflowStep {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  date?: string | null;
  isActive: boolean;
  isCompleted: boolean;
  isCancelled?: boolean;
}

interface WorkflowTimelineProps {
  status: string;
  orderDate: string;
  confirmedAt?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
  invoicedAt?: string | null;
  paidAt?: string | null;
  cancelledAt?: string | null;
}

const formatDate = (dateStr: string | null | undefined): string | null => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const WorkflowTimeline: React.FC<WorkflowTimelineProps> = ({
  status,
  orderDate,
  confirmedAt,
  shippedAt,
  deliveredAt,
  invoicedAt,
  paidAt,
  cancelledAt
}) => {
  const isCancelled = status === 'cancelled';

  const getStepStatus = (stepKey: string): { isActive: boolean; isCompleted: boolean } => {
    const statusOrder = ['pending', 'confirmed', 'shipped', 'delivered', 'invoiced', 'paid'];
    const currentIndex = statusOrder.indexOf(status);
    const stepIndex = statusOrder.indexOf(stepKey);

    if (isCancelled) {
      return { isActive: false, isCompleted: false };
    }

    return {
      isActive: stepKey === status,
      isCompleted: stepIndex < currentIndex
    };
  };

  const steps: WorkflowStep[] = [
    {
      key: 'pending',
      label: 'Créée',
      icon: Clock,
      date: orderDate,
      ...getStepStatus('pending')
    },
    {
      key: 'confirmed',
      label: 'Confirmée',
      icon: CheckCircle,
      date: confirmedAt || (getStepStatus('confirmed').isCompleted || getStepStatus('confirmed').isActive ? orderDate : null),
      ...getStepStatus('confirmed')
    },
    {
      key: 'shipped',
      label: 'Expédiée',
      icon: Truck,
      date: shippedAt,
      ...getStepStatus('shipped')
    },
    {
      key: 'delivered',
      label: 'Livrée',
      icon: Package,
      date: deliveredAt,
      ...getStepStatus('delivered')
    },
    {
      key: 'invoiced',
      label: 'Facturée',
      icon: FileText,
      date: invoicedAt,
      ...getStepStatus('invoiced')
    },
    {
      key: 'paid',
      label: 'Payée',
      icon: CreditCard,
      date: paidAt,
      ...getStepStatus('paid')
    }
  ];

  if (isCancelled) {
    return (
      <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="font-semibold text-red-600 dark:text-red-400">Commande annulée</p>
            {cancelledAt && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {formatDate(cancelledAt)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Desktop: Horizontal */}
      <div className="hidden lg:block">
        <div className="flex items-start justify-between">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isLast = index === steps.length - 1;

            return (
              <div key={step.key} className="flex-1 relative">
                <div className="flex flex-col items-center">
                  {/* Connector line */}
                  {!isLast && (
                    <div
                      className={`absolute top-5 left-1/2 w-full h-0.5 ${
                        step.isCompleted
                          ? 'bg-emerald-500'
                          : 'bg-slate-200 dark:bg-slate-700'
                      }`}
                    />
                  )}

                  {/* Step circle */}
                  <div
                    className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      step.isActive
                        ? 'bg-orange-500 text-white ring-4 ring-orange-200 dark:ring-orange-900/50 scale-110'
                        : step.isCompleted
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    <StepIcon className="w-5 h-5" />
                  </div>

                  {/* Label */}
                  <p
                    className={`mt-3 text-sm font-medium text-center ${
                      step.isActive
                        ? 'text-orange-600 dark:text-orange-400'
                        : step.isCompleted
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {step.label}
                  </p>

                  {/* Date */}
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 text-center h-4">
                    {step.date ? formatDate(step.date) : '—'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: Vertical */}
      <div className="lg:hidden space-y-0">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isLast = index === steps.length - 1;

          return (
            <div key={step.key} className="relative flex gap-4">
              {/* Connector line (vertical) */}
              {!isLast && (
                <div
                  className={`absolute left-5 top-10 w-0.5 h-full -translate-x-1/2 ${
                    step.isCompleted
                      ? 'bg-emerald-500'
                      : 'bg-slate-200 dark:bg-slate-700'
                  }`}
                />
              )}

              {/* Step circle */}
              <div
                className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                  step.isActive
                    ? 'bg-orange-500 text-white ring-4 ring-orange-200 dark:ring-orange-900/50'
                    : step.isCompleted
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                }`}
              >
                <StepIcon className="w-5 h-5" />
              </div>

              {/* Content */}
              <div className={`pb-6 ${isLast ? 'pb-0' : ''}`}>
                <p
                  className={`font-medium ${
                    step.isActive
                      ? 'text-orange-600 dark:text-orange-400'
                      : step.isCompleted
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-slate-400 dark:text-slate-500'
                  }`}
                >
                  {step.label}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {step.date ? formatDate(step.date) : '—'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowTimeline;
